import { type ChangeEvent, useEffect, useMemo, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { formatDate } from 'commons/utils/date'
import { removeDiacriticsAndSpecialCharacters } from 'commons/utils/string'
import { getUser } from 'commons/utils/user'
import {
  Accordion,
  Alert,
  Autocomplete,
  Breadcrumb,
  Card,
  Checkbox,
  DatePicker,
  Icon,
  Info,
  Input,
  LoadingModal,
  Modal,
  Page,
  SlimButton,
  Typography,
  UploadArea,
} from 'components'
import { ROUTES } from 'constants/urls'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import type { EdiFormValues, EdiVersion, IntlEDI } from 'services/batchOrder/interfaces'
import {
  clearGeneratedData,
  clearUploadData,
  deleteEdiUpload,
  getEdiOptions,
  getEdiVersionOptions,
  SendEdiOrders,
  sendEdiUpload,
} from 'store/BatchOrderContext/BatchOrderSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'
import * as yup from 'yup'

import Footer from '../components/Footer'
import ModalOrderOverview from '../components/ModalOrderOverview'
import UploadSpreadsheetTableError from '../components/UploadSpreadsheetTableError'
import { OverviewContent } from './components'
import * as S from './styles'

const International = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    loadingEdiOptions,
    ediOptions,
    loadingVersionOptions,
    versionOptions,
    loadingUpload,
    loadingDeletion,
    generatedEdiOrders,
    loadingSend,
    uploadData: { processId, ediErrors, uploadSuccess, hasOrders },
  } = useSelector((state) => state.batchOrder)

  const schema = yup.object().shape({
    billingMonth: yup.date().typeError('Data inválida').required(),
    checkVersion: yup.boolean(),
    edi: yup.object().typeError('Seleção obrigatória').required(),
    expectedDate: yup.date().typeError('Data inválida').required(),
    headerText: yup.string().required('Campo obrigatório'),
    itemText: yup.string().required('Campo obrigatório'),
    orderMonth: yup.date().typeError('Data inválida').required(),
    version: yup.object().when('checkVersion', {
      is: true,
      otherwise: yup.object().nullable(),
      then: yup.object().typeError('Seleção obrigatória').required(),
    }),
    warehouse: yup.string(),
  })

  const defaultValues: EdiFormValues = {
    billingMonth: '',
    checkVersion: false,
    edi: null,
    expectedDate: '',
    headerText: '',
    itemText: '',
    orderMonth: '',
    uploadedFile: null,
    version: null,
    warehouse: '',
  }

  const {
    control,
    watch,
    setValue,
    trigger,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const disableForm = useMemo(() => loadingUpload || !!processId, [loadingUpload, processId])
  const hasUploadSuccess = useMemo(() => !!processId && hasOrders, [processId, hasOrders])
  const hasUploadError = useMemo(() => !!processId && !!ediErrors.length, [processId, hasOrders])

  const selectedEDI = watch('edi')
  const checkVersion = watch('checkVersion')
  const file = watch('uploadedFile')
  const fileName = useMemo(() => file?.name ?? '', [file])

  const [ediSearch, setEdiSearch] = useState('')
  const [versionSearch, setVersionSearch] = useState('')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [modalDeleteOrders, setModalDeleteOrders] = useState(false)
  const [modalOrderOverview, setModalOrderOverview] = useState(false)

  const onSubmit = handleSubmit((data) => {
    dispatch(sendEdiUpload(data, setUploadProgress))
  })

  useEffect(() => {
    dispatch(getEdiOptions())

    return () => {
      dispatch(clearUploadData())
    }
  }, [])

  useEffect(() => {
    if (selectedEDI) {
      dispatch(getEdiVersionOptions(selectedEDI.cd_edi))
    }
  }, [selectedEDI])

  useEffect(() => {
    trigger('version')
  }, [checkVersion])

  useEffect(() => {
    if (generatedEdiOrders.id_prcs_edi) {
      setModalOrderOverview(true)
    }
  }, [generatedEdiOrders])

  const handleOrderDeletion = () => {
    setModalDeleteOrders(false)
    dispatch(
      deleteEdiUpload({
        cd_edi: selectedEDI?.cd_edi,
        cd_usua_alte: getUser(),
        id_prcs_edi: processId,
      }),
    )
  }

  const handleOrderSend = () => {
    dispatch(
      SendEdiOrders({
        cd_edi: selectedEDI?.cd_edi,
        id_prcs_edi: processId,
      }),
    )
  }

  const getVersionPlaceholder = () => {
    if (!selectedEDI) return 'Selecione um EDI'
    if (loadingVersionOptions) return 'Carregando opções...'
    if (!checkVersion) return 'Ative o checkbox'
    return 'Clique para selecionar'
  }

  const InsertionHeader = () => (
    <S.ColWrapper>
      <Typography design="hav-h6-text">Inserção de material</Typography>
      <Typography design="hav-h7-text">
        Após preencher os campos acima, carregue a planilha e solicite os materiais.
      </Typography>
    </S.ColWrapper>
  )

  return (
    <Page fullwidth>
      <Breadcrumb />
      <S.Wrapper>
        <Accordion summaryContent={<S.Title>Pedido internacional</S.Title>}>
          <Card className="form">
            <S.Form onSubmit={onSubmit}>
              <S.Field span={4}>
                <Controller
                  control={control}
                  name={'edi'}
                  render={({ field }) => (
                    <Autocomplete<IntlEDI>
                      browserAutocomplete="off"
                      data={ediOptions}
                      disabled={disableForm || loadingEdiOptions || !ediOptions.length}
                      input={ediSearch}
                      inputId={field.name}
                      label="EDI"
                      objDescription="ds_edi"
                      objId="cd_edi"
                      placeHolder={
                        loadingEdiOptions ? 'Carregando opções...' : 'Clique para selecionar'
                      }
                      ref={field.ref}
                      resetOnBlur
                      setInput={setEdiSearch}
                      setOptionSelected={(e) => {
                        setValue('version', null)
                        setVersionSearch('')
                        field.onChange(e)
                      }}
                    />
                  )}
                />
              </S.Field>
              <S.Field span={1}>
                <S.CubageWrapper>
                  <Typography design="hav-h8-text">M² Máxima OV</Typography>
                  <Typography design="hav-default-03-text">
                    {selectedEDI?.vl_maxi_metr_cubi ?? '-'}
                  </Typography>
                </S.CubageWrapper>
              </S.Field>
              <S.Field span={1}>
                <S.VersionCheckWrapper>
                  <Controller
                    control={control}
                    name={'checkVersion'}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        disabled={disableForm || !selectedEDI}
                        label="Definir versão"
                        onChange={(event, checked) => {
                          if (!checked) {
                            setValue('version', null)
                            setVersionSearch('')
                          }
                          field.onChange(event)
                        }}
                        size="small"
                      />
                    )}
                  />
                </S.VersionCheckWrapper>
              </S.Field>
              <S.Field>
                <Controller
                  control={control}
                  name={'version'}
                  render={({ field, fieldState }) => (
                    <Autocomplete<EdiVersion>
                      browserAutocomplete="off"
                      data={versionOptions}
                      disabled={
                        disableForm ||
                        !checkVersion ||
                        loadingVersionOptions ||
                        !versionOptions.length
                      }
                      error={fieldState.error?.message}
                      input={versionSearch}
                      inputAdornment={
                        <Info iconSize={12}>
                          Caso o campo de versão não seja preenchido, o sistema selecionará
                          automaticamente a coleção atual.
                        </Info>
                      }
                      inputId={field.name}
                      label="Versão"
                      objDescription="ds_edi_vers"
                      objId="cd_edi_vers"
                      placeHolder={getVersionPlaceholder()}
                      ref={field.ref}
                      resetOnBlur={field.value !== null}
                      setInput={setVersionSearch}
                      setOptionSelected={field.onChange}
                    />
                  )}
                />
              </S.Field>
              <S.Field>
                <Controller
                  control={control}
                  name={'warehouse'}
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled={disableForm || !selectedEDI}
                      label="Armazém"
                      maxLenghtPosition="top"
                      maxLength={25}
                      onChange={(e) =>
                        field.onChange(removeDiacriticsAndSpecialCharacters(e.target.value))
                      }
                      placeholder={!selectedEDI ? 'Selecione um EDI' : 'Clique para escrever'}
                      showMaxLength
                    />
                  )}
                />
              </S.Field>
              <S.Field>
                <Controller
                  control={control}
                  name={'orderMonth'}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      disabled={disableForm || !selectedEDI}
                      disableMaskedInput={false}
                      error={fieldState.error?.message}
                      height={30}
                      inputFormat="MM/YYYY"
                      label="Mês do pedido"
                      onChange={(date) => field.onChange(formatDate(date, 'YYYY-MM'))}
                      placeholder={!selectedEDI ? 'Selecione um EDI' : 'mm/aaaa'}
                      value={field.value}
                      views={['month', 'year']}
                    />
                  )}
                />
              </S.Field>
              <S.Field>
                <Controller
                  control={control}
                  name={'billingMonth'}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      disabled={disableForm || !selectedEDI}
                      disableMaskedInput={false}
                      error={fieldState.error?.message}
                      height={30}
                      inputFormat="MM/YYYY"
                      label="Mês do faturamento"
                      onChange={(date) => field.onChange(formatDate(date, 'YYYY-MM'))}
                      placeholder={!selectedEDI ? 'Selecione um EDI' : 'mm/aaaa'}
                      value={field.value}
                      views={['month', 'year']}
                    />
                  )}
                />
              </S.Field>
              <S.Field>
                <Controller
                  control={control}
                  name={'expectedDate'}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      disabled={disableForm || !selectedEDI}
                      disableMaskedInput={false}
                      error={fieldState.error?.message}
                      height={30}
                      inputFormat="DD/MM/YYYY"
                      label="Data prevista"
                      onChange={(date) => field.onChange(formatDate(date, 'YYYY-MM-DD'))}
                      placeholder={!selectedEDI ? 'Selecione um EDI' : 'dd/mm/aaaa'}
                      value={field.value}
                    />
                  )}
                />
              </S.Field>
              <S.Field span={4}>
                <Controller
                  control={control}
                  name={'headerText'}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      disabled={disableForm || !selectedEDI}
                      error={fieldState.error?.message}
                      label="Cabeçalho"
                      maxLenghtPosition="top"
                      maxLength={140}
                      onChange={(e) =>
                        field.onChange(removeDiacriticsAndSpecialCharacters(e.target.value))
                      }
                      placeholder={!selectedEDI ? 'Selecione um EDI' : 'Clique para escrever'}
                      showMaxLength
                    />
                  )}
                />
              </S.Field>
              <S.Field span={4}>
                <Controller
                  control={control}
                  name={'itemText'}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      disabled={disableForm || !selectedEDI}
                      error={fieldState.error?.message}
                      label="Item"
                      maxLenghtPosition="top"
                      maxLength={140}
                      onChange={(e) =>
                        field.onChange(removeDiacriticsAndSpecialCharacters(e.target.value))
                      }
                      placeholder={!selectedEDI ? 'Selecione um EDI' : 'Clique para escrever'}
                      showMaxLength
                    />
                  )}
                />
              </S.Field>
            </S.Form>
          </Card>
        </Accordion>

        {hasUploadError && (
          <Alert
            fullWidth
            kind="error"
            message="Foram identificados alguns erros na planilha. Verifique os detalhes abaixo e faça as correções necessárias antes de reenviá-la."
            title="Erros na planilha!"
          />
        )}
        {hasUploadSuccess && (
          <Alert
            fullWidth
            kind="info"
            message='Clique em "solicitar" para gerar as OVs. Caso contrário, os pedidos ficarão em aberto na área de gerenciamento.'
            title="Pedido em aberto!"
          />
        )}
        {!processId && (
          <Card>
            <S.InsertionWrapper>
              <InsertionHeader />
              {isValid && (
                <>
                  <S.Divider />
                  <Controller
                    control={control}
                    defaultValue={{} as File}
                    name="uploadedFile"
                    render={({ field: { name, onChange } }) => (
                      <UploadArea
                        accept="text/csv"
                        id="uploadedFile"
                        loading={loadingUpload}
                        loadingProgress={uploadProgress}
                        name={name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (e.target.files) {
                            onChange(e.target.files[0])
                            onSubmit()
                            e.target.value = ''
                          }
                        }}
                        uploadAreaText={
                          <>
                            <strong>Clique aqui</strong> ou arraste o arquivo da sua planilha para
                            enviá-la.
                          </>
                        }
                      />
                    )}
                  />
                </>
              )}
            </S.InsertionWrapper>
          </Card>
        )}
        {hasUploadError && (
          <Card>
            <S.ErrorWrapper>
              <div>
                <SlimButton
                  content="Voltar"
                  icon={<Icon icon="undo" size="15" />}
                  onClick={() => dispatch(clearUploadData())}
                  type="button"
                  variant="primary"
                />
              </div>
              <S.UploadedFileViewer>
                <Icon color={theme.colors.primary.main} icon="paper-clip" size="15" />
                <Typography design="hav-h7-text">{fileName}</Typography>
              </S.UploadedFileViewer>
              <UploadSpreadsheetTableError data={ediErrors} />
            </S.ErrorWrapper>
          </Card>
        )}
        {hasUploadSuccess && (
          <Card>
            <S.InsertionWrapper>
              <InsertionHeader />
              <S.Divider />
              <S.UploadedFileViewer>
                <Icon color={theme.colors.primary.main} icon="paper-clip" size="15" />
                <Typography design="hav-h7-text">{fileName}</Typography>
              </S.UploadedFileViewer>
              <Footer
                amountOfBoxes={uploadSuccess.boxVol}
                amountOfPairs={uploadSuccess.pairVol}
                loading={loadingDeletion || loadingSend}
                onDelete={() => setModalDeleteOrders(true)}
                onSubmit={handleOrderSend}
                totalValue={uploadSuccess.totalVal}
              />
            </S.InsertionWrapper>
          </Card>
        )}
      </S.Wrapper>
      <Modal
        description="Esta ação excluirá o pedido em aberto e não poderá ser desfeita."
        open={modalDeleteOrders}
        setOpen={setModalDeleteOrders}
        showAction={false}
        title="Excluir"
      >
        <S.DeleteOrdersActions>
          <SlimButton
            content="Voltar"
            onClick={() => setModalDeleteOrders(false)}
            variant="ghost"
          />
          <SlimButton
            content="Excluir"
            loading={loadingDeletion}
            onClick={handleOrderDeletion}
            variant="danger"
          />
        </S.DeleteOrdersActions>
      </Modal>
      {loadingSend && <LoadingModal loadingText="Solicitando materiais..." />}
      {!!generatedEdiOrders.id_prcs_edi && (
        <ModalOrderOverview
          onConfirm={() => {
            dispatch(clearGeneratedData())
            navigate(`${ROUTES.manage.base}`)
          }}
          onReturn={() => {
            dispatch(clearGeneratedData())
            setModalOrderOverview(false)
          }}
          open={modalOrderOverview}
          setOpen={setModalOrderOverview}
        >
          <OverviewContent />
        </ModalOrderOverview>
      )}
    </Page>
  )
}

export default International
